import { useEffect, useState } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { GET_QUOTE, FINAL_FORMS_BY_INTERNAL_NAME } from '../../../queries';
import { useLazyQuery } from '@apollo/client';
import { ResumeError } from './ResumeError';
import { usePolicyStreamGoogleAnalytics } from '../../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../../hooks/usePolicyStreamGoogleAnalytics/types';
import { POLICYSTREAM_FORMS } from '../constants';
import { styled } from '@mui/material';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import Typography from '@mui/material/Typography';
import { QuoteQuery } from '../../../gql/graphql';
import { QuoteWizardForm } from '../../QuoteWizard/classes';
import { isNotNullish } from '../../../util';
import { ProgramBuilderForm } from '../../FormManager/classes';
import { Language } from '../../../Typescript/classes';
import { SupportLink } from '../../common/SupportLink';
import { useDispatch } from 'react-redux';
import { policystreamStore } from '../../../store';
import { mapAnswerToReduxState } from '../../QuoteWizard/QuoteResume/utility';
import { BackendCompletedForm } from '../../../Typescript/backend/classes';
import { QuoteWizardAnswerInstance } from '../../QuoteWizard/classes';
import { useSetPolicyStreamSaveButtonVisibility } from '../../../hooks';
import { generateSubmitPolicyStreamFormAnswersThunk } from '../../../store/policystreamStore';
import { useAuth } from '@calefy-inc/authentication';
import { AnswerInstanceType } from '../../../gql/graphql';
import { makePolicystreamUrl } from '../policyStreamConstants';
import { policyStreamPaths } from '../policyStreamConstants';
const {
  actions: {
    addForms,
    setSelectedPlan,
    updateRecommendation,
    setPlanPaymentFrequency,
    setRequestedRecommendation,
    setStepStatus,
    setStepRelevance,
    setCurrentQuoteUuid,
  },
} = policystreamStore;

const areAllFormAnswersComplete = (answers: Array<AnswerInstanceType>) => {
  for (const answer of answers) {
    if (answer.value === '' || answer.value === 'N/A') {
      return false;
    }
    if (answer.subAnswers && answer.subAnswers.length > 0) {
      if (!areAllFormAnswersComplete(answer.subAnswers)) {
        return false;
      }
    }
  }

  return true;
};

interface PolicyStreamResumeProps extends RouteComponentProps {
  uuid?: string;
}
export const PolicyStreamResume = ({ uuid }: PolicyStreamResumeProps) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  const { token } = useAuth();
  useSetPolicyStreamSaveButtonVisibility(false);
  const dispatch = useDispatch();
  const [quote, setQuote] = useState<NonNullable<QuoteQuery['quote']>>();

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.Resume,
      EventActions.PageLoad,
      'PolicyStream Resume Page',
    );
  }, [myAnalytics]);
  const [forms, setForms] = useState<Array<QuoteWizardForm>>([]);
  const [
    getQuote,
    { loading: getQuoteLoading, error: getQuoteError, data: getQuoteData },
  ] = useLazyQuery(GET_QUOTE);
  const [
    getForms,
    { loading: getFormsLoading, error: getFormsError, data: getFormsData },
  ] = useLazyQuery(FINAL_FORMS_BY_INTERNAL_NAME);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const resumeTo = searchParams.get('resumeTo');

  // get the quote and forms as soon as we load
  useEffect(() => {
    if (uuid) {
      getQuote({
        variables: {
          token,
          uID: uuid,
        },
      });
      getForms({
        variables: {
          internalNames: Object.values(POLICYSTREAM_FORMS),
        },
      });
    }
  }, [uuid, token]);

  // load the quote in state once we know (plus set any selected plan in state)
  useEffect(() => {
    if (getQuoteData?.quote) {
      setQuote(getQuoteData.quote);

      // set current quote uuid
      if (getQuoteData.quote.uniqueID) {
        dispatch(
          setCurrentQuoteUuid({
            uuid: getQuoteData.quote.uniqueID,
          }),
        );
      }

      // set plan
      if (getQuoteData.quote.policystreamPlan) {
        dispatch(setSelectedPlan(getQuoteData.quote.policystreamPlan));
      }

      // set the payment frequency
      if (getQuoteData.quote.QuotePolicystreamPlanPaymentFrequency) {
        const frequency =
          getQuoteData.quote.QuotePolicystreamPlanPaymentFrequency;
        dispatch(setPlanPaymentFrequency(frequency));
      }

      if (getQuoteData.quote.policystreamRecommendation) {
        dispatch(
          updateRecommendation(getQuoteData.quote.policystreamRecommendation),
        );
        dispatch(setRequestedRecommendation(true));
      }
    }
  }, [getQuoteData]);

  // load the forms once we have them
  useEffect(() => {
    if (getFormsData?.someFinalForms) {
      setForms(
        getFormsData.someFinalForms
          .filter(isNotNullish)
          .map((backend) =>
            ProgramBuilderForm.generateFromBackendResponse(
              backend,
            ).toQuoteWizardForm(Language.english),
          ),
      );
    }
  }, [getFormsData]);

  // once we have the forms, set them in state
  useEffect(() => {
    dispatch(addForms({ forms }));
  }, [forms]);

  // forms & quote -> generate the answers
  useEffect(() => {
    if (quote && forms.length > 0) {
      for (let quoteWizardForm of forms) {
        const matching = quote.completedForms.find(
          (completedForm) =>
            completedForm.internalName === quoteWizardForm.internalName,
        );
        if (!matching) {
          continue;
        }

        const backendCompletedForm =
          BackendCompletedForm.generateFromBackendResponse(matching);
        const reduxAnswers = backendCompletedForm.answers.reduce(
          (acc: Array<QuoteWizardAnswerInstance>, answer) => {
            const reduxStateAnswer = mapAnswerToReduxState(
              answer,
              quoteWizardForm.questionInstances,
              Language.english,
            );
            if (reduxStateAnswer) {
              acc.push(reduxStateAnswer);
            }
            return acc;
          },
          [],
        );
        const reduxAnsweredForm = {
          form: quoteWizardForm,
          answers: reduxAnswers,
        };
        dispatch(generateSubmitPolicyStreamFormAnswersThunk(reduxAnsweredForm));
      }

      /* --- Where are we resuming to? --- */
      let urlSlug: string = '';
      // have they filled out the basic information?
      const hasBasicInformation = !!quote.completedForms.find(
        (form) => form.internalName === POLICYSTREAM_FORMS.basicInfo,
      );
      if (
        resumeTo !== policyStreamPaths.baseInformation.slug ||
        hasBasicInformation
      ) {
        dispatch(
          setStepStatus({
            name: 'Basic Information',
            status: 'COMPLETED',
          }),
        );
      }
      urlSlug = policyStreamPaths.baseInformation.slug;
      //console.log({ resumeTo, hasBasicInformation, urlSlug });

      // do they have a needs analysis?
      const hasNeedsAnalysis = !!quote.completedForms.find(
        (form) => form.internalName === POLICYSTREAM_FORMS.needsAnalysis,
      );
      if (!hasNeedsAnalysis) {
        dispatch(
          setStepRelevance({
            name: 'Needs Analysis',
            relevant: false,
          }),
        );
      }
      if (hasNeedsAnalysis) {
        dispatch(
          setStepStatus({ name: 'Needs Analysis', status: 'COMPLETED' }),
        );
        urlSlug = policyStreamPaths.recommendations.slug;
      }
      //console.log({ resumeTo, hasNeedsAnalysis, urlSlug });

      // have they selected a plan already?
      const hasPlan = !!quote.policystreamPlan;
      if (
        resumeTo === policyStreamPaths.planSpecificQuestions.slug ||
        resumeTo === policyStreamPaths.postPlanSelectionInformation.slug ||
        resumeTo === policyStreamPaths.review.slug ||
        hasPlan
        // resumeTo !== policyStreamPaths.baseInformation.slug &&
        // resumeTo !== policyStreamPaths.personalize.slug &&
        // resumeTo !== policyStreamPaths.recommendations.slug &&
        // resumeTo !== policyStreamPaths.planSelection.slug
      ) {
        dispatch(
          setStepStatus({ name: 'Plan Selection', status: 'COMPLETED' }),
        );
        urlSlug = policyStreamPaths.planSelection.slug;
      }
      //console.log('plan selection:', { resumeTo, hasPlan, urlSlug });

      const hasBasicClientInfoForm = !!quote.completedForms.find(
        (form) => form.internalName === POLICYSTREAM_FORMS.basicClientInfo,
      );
      // need to check both because we automatically fill in parts of this form
      if (hasPlan && hasBasicClientInfoForm) {
        urlSlug = policyStreamPaths.postPlanSelectionInformation.slug;
      }
      //console.log({ resumeTo, hasBasicClientInfoForm, urlSlug });

      const hasApplicationForm = !!quote.completedForms.find(
        (form) => form.internalName === POLICYSTREAM_FORMS.applicationForm,
      );
      if (hasApplicationForm) {
        dispatch(
          setStepStatus({ name: 'Application Form', status: 'COMPLETED' }),
        );
        urlSlug = policyStreamPaths.planSpecificQuestions.slug;
      }
      //console.log({ resumeTo, hasApplicationForm, urlSlug });

      if (resumeTo === policyStreamPaths.review.slug) {
        urlSlug = policyStreamPaths.review.slug;
      }
      //console.log('final slug: ', { urlSlug });

      let url: string = makePolicystreamUrl(urlSlug);
      //console.log('About to navigate to', url);

      navigate(url);
    }
  }, [forms, quote]);

  return (
    <PolicyStreamResumeContainer>
      <PolicyStreamResumeContentsContainer>
        <Typography variant='h3' component='h1'>
          Loading Application
        </Typography>
        {!getQuoteError && !getFormsError ? (
          <Typography>We're just getting your application now.</Typography>
        ) : null}
        {getQuoteLoading || getFormsLoading ? <LoadingScreen /> : null}
        {quote && !quote.isPolicystreamApplication ? (
          <NotPolicyStreamApplication />
        ) : null}
        <ResumeError error={getQuoteError || getFormsError} />
      </PolicyStreamResumeContentsContainer>
    </PolicyStreamResumeContainer>
  );
};

const PolicyStreamResumeContainer = styled('section')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});
const PolicyStreamResumeContentsContainer = styled('div')({
  maxWidth: '1000px',
});

const NotPolicyStreamApplication = () => {
  return (
    <>
      <Typography variant='h4'>Not a PolicyStream Application</Typography>
      <Typography>
        Unfortunately, this application cannot be loaded as it is not a
        PolicyStream application. Someone may have sent this to you
        accidentally. If you believe this to be an error, please contact our
        support team at <SupportLink subject='Error loading application' />. Be
        sure to include any relevant details!
      </Typography>
    </>
  );
};
